import { useState } from "react";
import { moderationApi, ModerationApiError, type ReasonCategory, type TargetType } from "../../lib/moderation-api";
import { cn } from "../cn";

/**
 * New Moderation modal — Hide / Emergency Hide a target (Macro #20 §24). The
 * initial moderation requires a mandatory Admin Note. Server enforces the same;
 * this only front-loads the affordance.
 */
export function AdminHideForm({
  reasons,
  onClose,
  onCreated,
}: {
  reasons: ReasonCategory[];
  onClose: () => void;
  onCreated: (caseId: string) => void;
}) {
  const [targetType, setTargetType] = useState<TargetType>("territory");
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState(reasons[0]?.key ?? "");
  const [note, setNote] = useState("");
  const [emergency, setEmergency] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!targetId.trim()) { setErr("Target id is required."); return; }
    if (!reason) { setErr("Select a reason category."); return; }
    if (!note.trim()) { setErr("An Admin Note is required for the initial moderation."); return; }
    setBusy(true);
    setErr(null);
    try {
      const res = await moderationApi.hide({
        target_type: targetType,
        target_id: targetId.trim(),
        reason_category: reason,
        admin_note: note.trim(),
        emergency,
      });
      onCreated(res.case.case_id);
    } catch (e) {
      setErr(e instanceof ModerationApiError ? `${e.code}: ${e.message}` : "Moderation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-hide-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="panel-chamfer w-[min(520px,95%)] relative"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "inset 0 0 0 1px var(--neon-coral-ring), 0 0 44px -10px var(--neon-coral-glow)" }}
      >
        <div className="chamfer-strip">
          <span className="strip-dot" style={{ background: "var(--neon-coral)" }} aria-hidden />
          <span>New Moderation</span>
        </div>
        <div className="p-5 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target type">
              <select
                data-testid="admin-hide-target-type"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as TargetType)}
                className="w-full h-9 rounded-md border border-border-soft bg-obsidian px-2 font-mono text-[11px] text-text-primary focus:outline-none focus:border-border-accent"
              >
                <option value="territory">territory</option>
                <option value="artwork">artwork</option>
              </select>
            </Field>
            <Field label="Reason">
              <select
                data-testid="admin-hide-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full h-9 rounded-md border border-border-soft bg-obsidian px-2 font-mono text-[11px] text-text-primary focus:outline-none focus:border-border-accent"
              >
                {reasons.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label={targetType === "territory" ? "Identity id" : "Artwork id"}>
            <input
              data-testid="admin-hide-target-id"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="target id"
              className="w-full h-9 rounded-md border border-border-soft bg-obsidian px-3 font-mono text-[11px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-accent"
            />
          </Field>
          <Field label="Admin Note (required)">
            <textarea
              data-testid="admin-hide-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-md border border-border-soft bg-obsidian px-3 py-2 text-[12px] text-text-primary focus:outline-none focus:border-border-accent"
            />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              data-testid="admin-hide-emergency"
              type="checkbox"
              checked={emergency}
              onChange={(e) => setEmergency(e.target.checked)}
              className="accent-[color:var(--neon-coral)]"
            />
            <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-secondary">
              Emergency Hide
            </span>
          </label>

          {err && <div data-testid="admin-hide-error" className="font-mono text-[11px] text-state-danger">{err}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="admin-hide-cancel"
              onClick={onClose}
              className="inline-flex items-center h-9 px-4 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-text-muted border border-border-soft hover:text-text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="admin-hide-submit"
              onClick={submit}
              disabled={busy}
              className={cn(
                "inline-flex items-center h-9 px-5 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em]",
                "text-neon-coral border border-[color:var(--neon-coral-ring)] hover:bg-[color:var(--neon-coral-soft)] transition-colors disabled:opacity-40",
              )}
            >
              {busy ? "Moderating…" : emergency ? "Emergency Hide" : "Hide"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}
